import { Menu } from 'lucide-react';
import { Button, Drawer, useOverlayState } from '@heroui/react';
import { useAuth } from '../hooks/useAuth';
import { useCurrentCondominium } from '../hooks/useCurrentCondominium';
import { useSwitchCondominium } from '../hooks/useSwitchCondominium';
import { AppBrand } from '../components/ui/app-brand';
import { SwitchCondominiumModal } from '../components/ui/switch-condominium-modal';
import { UserMenuDropdown } from '../components/ui/user-menu-dropdown';
import { NotificationsMenu } from '../features/notifications/components/notifications-menu';
import { AppNavLinks } from './app-nav-links';
import { getMobileDrawerSections } from './nav-items';
import { navDrawerDialogClassName } from '../components/ui/side-drawer-classes';

export function AppTopbar() {
  const { authUser, userDoc } = useAuth();
  const { label: condominiumLabel, isLoading: isCondominiumLoading } = useCurrentCondominium();
  const {
    modal: switchCondominiumModal,
    switching,
    condominiums,
    condominium,
    openModal: openSwitchCondominiumModal,
    handleSelect: handleSwitchCondominium,
    canOpenModal: canOpenSwitchCondominium,
  } = useSwitchCondominium();
  const menuState = useOverlayState();

  const displayName =
    userDoc?.displayName ?? authUser?.displayName ?? authUser?.email ?? 'Usuário';

  const photoUrl = userDoc?.photoURL ?? authUser?.photoURL ?? null;
  const isGlobalAdmin = userDoc?.isGlobalAdmin ?? false;
  const isCondominiumManager = userDoc?.isCondominiumManager ?? false;
  const mobileDrawerSections = getMobileDrawerSections({
    isGlobalAdmin,
    isCondominiumManager,
  });
  const showMobileMenu = mobileDrawerSections.length > 0;

  const mobileTenantLabel = isCondominiumLoading
    ? 'Carregando…'
    : (condominiumLabel ?? 'Sem condomínio');

  return (
    <>
      {/*
        sticky (não fixed): no iOS 26/27 o Liquid Glass amostra sticky+fundo opaco
        e deixa a status bar sólida; fixed mantém o blur translúcido.
      */}
      <header className="sticky top-0 z-30 shrink-0 border-b border-separator bg-surface pt-[env(safe-area-inset-top)] [background-color:var(--surface)]">
        <div className="flex h-14 items-center gap-2 px-4 md:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-2 md:hidden">
            {showMobileMenu ? (
              <Button
                isIconOnly
                variant="ghost"
                className="shrink-0"
                aria-label="Abrir menu administrativo"
                onPress={menuState.open}
              >
                <Menu size={20} />
              </Button>
            ) : null}
            {canOpenSwitchCondominium ? (
              <Button
                variant="ghost"
                className="h-auto min-w-0 flex-1 justify-start gap-2 px-1 py-1"
                aria-label="Trocar condomínio"
                isDisabled={isCondominiumLoading}
                onPress={openSwitchCondominiumModal}
              >
                <AppBrand
                  className="min-w-0 pointer-events-none"
                  label={mobileTenantLabel}
                  labelClassName="text-sm font-semibold text-foreground"
                  iconSize={18}
                  markClassName="size-8 rounded-md"
                />
              </Button>
            ) : (
              <AppBrand
                className="min-w-0 flex-1"
                label={mobileTenantLabel}
                labelClassName="text-sm font-semibold text-foreground"
                iconSize={18}
                markClassName="size-8 rounded-md"
              />
            )}
          </div>

          <div className="hidden flex-1 md:block" aria-hidden />

          <div className="flex shrink-0 items-center gap-1">
            <NotificationsMenu />
            <UserMenuDropdown
              displayName={displayName}
              email={authUser?.email}
              photoUrl={photoUrl}
              canOpenSwitchCondominium={canOpenSwitchCondominium}
              onOpenSwitchCondominium={openSwitchCondominiumModal}
            />
          </div>
        </div>
      </header>

      <SwitchCondominiumModal
        state={switchCondominiumModal}
        condominiums={condominiums}
        selectedCondominiumId={condominium?.id}
        switching={switching}
        onSelect={handleSwitchCondominium}
      />

      {showMobileMenu ? (
        <Drawer state={menuState}>
          <Drawer.Backdrop isDismissable>
            <Drawer.Content placement="left">
              <Drawer.Dialog className={navDrawerDialogClassName}>
                <Drawer.Header>
                  <Drawer.Heading>Administração</Drawer.Heading>
                  <Drawer.CloseTrigger />
                </Drawer.Header>
                <Drawer.Body>
                  <AppNavLinks sections={mobileDrawerSections} onNavigate={menuState.close} />
                </Drawer.Body>
              </Drawer.Dialog>
            </Drawer.Content>
          </Drawer.Backdrop>
        </Drawer>
      ) : null}
    </>
  );
}
